import { useQuery } from '@tanstack/react-query';
import { Leader, type LeaderAgentRow, type AgentLiveStatus, type AgentProfile, type LiveCall } from './api';
import type { BreakReason } from '@/store/softphone';
import { useBackendOnline } from '@/store/connection';

/**
 * Takım lideri canlı agent listesi — 15s polling. Backend xapi durumunu +
 * günlük metriği birleştirir. Periodic state poller (roadmap step 5) devreye
 * girince WS invalidation eklenecek, şimdilik polling yeterli.
 */
export function useLeaderAgents() {
  const backendOnline = useBackendOnline();
  return useQuery<LeaderAgentRow[]>({
    queryKey: ['leader-agents'],
    queryFn: Leader.listAgents,
    refetchInterval: 15_000,
    staleTime: 10_000,
    enabled: backendOnline, // backend offline'ken poll'u durdur (WS push zaten gelmez)
  });
}

/**
 * Canlı çağrı board'u (supervisor monitoring — "Şu an konuşanlar"). Veri tamamen
 * WS push'tan ('calls:live' → AppShell ['live-calls'] cache'ine yazar); bu hook
 * sadece o cache'i okur, kendi fetch'i YOK (enabled:false). Agent softphone'ları
 * call_event push'ladıkça anlık güncellenir.
 */
export function useLiveCalls() {
  return useQuery<LiveCall[]>({
    queryKey: ['live-calls'],
    queryFn: () => [],
    enabled: false,
    staleTime: Infinity,
    initialData: [],
  });
}

/** Statü → görsel config (label + renkler). Tablo + filtre chip'leri kullanır. */
export const STATUS_DISPLAY: Record<AgentLiveStatus, { label: string; dot: string; chip: string }> = {
  'hazir':       { label: 'Hazır',        dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  'mola':        { label: 'Mola',         dot: 'bg-amber-500',   chip: 'bg-amber-50 text-amber-700 ring-amber-200' },
  'yemek':       { label: 'Yemek',        dot: 'bg-orange-500',  chip: 'bg-orange-50 text-orange-700 ring-orange-200' },
  'dis-arama':   { label: 'Dış Arama',    dot: 'bg-brand-500',   chip: 'bg-brand-50 text-brand-700 ring-brand-200' },
  'ozel':        { label: 'Özel',         dot: 'bg-violet-500',  chip: 'bg-violet-50 text-violet-700 ring-violet-200' },
  'kuyruk-disi': { label: 'Kuyruk Dışı',  dot: 'bg-slate-400',   chip: 'bg-slate-50 text-slate-600 ring-slate-200' },
  'cevrimdisi':  { label: 'Çevrimdışı',   dot: 'bg-slate-300',   chip: 'bg-slate-50 text-slate-400 ring-slate-200' },
  'bilinmiyor':  { label: 'Bilinmiyor',   dot: 'bg-slate-300',   chip: 'bg-slate-50 text-slate-400 ring-slate-200' },
};

/**
 * BreakReason → 3CX profile + ProfileMessage. softphone.ts'teki breakMap ile
 * BİREBİR aynı olmalı (eski IGDAS projesi kanıtlı mapping). Lider uzaktan mola
 * atarken bunu kullanır.
 */
export const REASON_TO_PROFILE: Record<BreakReason, { profile: AgentProfile; msg: string }> = {
  'mola':          { profile: 'Away',          msg: '' },
  'yemek':         { profile: 'Out of office', msg: '' },
  'dis-arama':     { profile: 'Custom 1',      msg: '' },
  'egitim':        { profile: 'Custom 2',      msg: 'Eğitim' },
  'toplanti':      { profile: 'Custom 2',      msg: 'Toplantı' },
  'sinav':         { profile: 'Custom 2',      msg: 'Sınav' },
  'soru':          { profile: 'Custom 2',      msg: 'Soru Molası' },
  'geri-bildirim': { profile: 'Custom 2',      msg: 'Geri Bildirim' },
};

/** "HH:MM" — ISO timestamp'tan saat:dakika (login zamanı gösterimi). */
export function fmtClock(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
